import { ContentItemBox } from 'ContentItemBox';
import {
    type ProjectGroupMembershipSansProjectGroup,
    ProjectGroupSetupModal,
    ViewProjectGroupModal,
} from 'ProjectGroup';
import { ProjectSubmissionFormModal } from 'ProjectSubmission';
import { type FC, memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPendingFocusedProjectRequirementIdentifier, useForceExpanded } from 'StudentDashboardSlice';
import { type ProjectContentBoxProps, type ProjectContentBoxPresentationProps } from './ProjectContentBox.types';
import { useProjectContentBoxExpandedContent } from './useProjectContentBoxExpandedContent';
import { useProjectImage } from './useProjectImage';

export const ProjectContentBoxPresentation: FC<ProjectContentBoxPresentationProps> = memo(
    ({
        title,
        src,
        fullWidth,
        expandedContent,
        endIcon,
        projectRequirementIdentifier,
        initiallyExpanded,
        itemId,
        forceExpanded,
        titleSize = 'base',
        padding,
        onPress,
    }) => (
        <ContentItemBox
            title={title}
            src={src}
            fullWidth={fullWidth}
            imageSize={10}
            desktopImageSize={7.5}
            height={18}
            desktopHeight={14.5}
            titleSize={titleSize}
            endIcon={endIcon}
            padding={padding}
            identifierClassName={projectRequirementIdentifier}
            initiallyExpanded={initiallyExpanded}
            itemId={itemId}
            forceExpanded={forceExpanded}
            {...(onPress ? { onPress } : { expandedContent })}
        />
    ),
);

type ProjectSubmissionModalState = {
    isOpen: boolean;
    projectGroupMembership: ProjectGroupMembershipSansProjectGroup | null;
};

type ViewProjectGroupModalState = {
    isOpen: boolean;
    projectGroupMembership: ProjectGroupMembershipSansProjectGroup | null;
};

const useProjectModals = () => {
    const [projectSubmissionModalState, setProjectSubmissionModalState] = useState<ProjectSubmissionModalState>({
        isOpen: false,
        projectGroupMembership: null,
    });
    const [isProjectGroupSetupModalOpen, setProjectGroupSetupModalOpen] = useState(false);
    const [viewProjectGroupModalState, setViewProjectGroupModalState] = useState<ViewProjectGroupModalState>({
        isOpen: false,
        projectGroupMembership: null,
    });

    const openProjectGroupSetupModal = useCallback(() => setProjectGroupSetupModalOpen(true), []);
    const closeProjectGroupSetupModal = useCallback(() => setProjectGroupSetupModalOpen(false), []);

    const openViewProjectGroupModal = useCallback((projectGroupMembership: ProjectGroupMembershipSansProjectGroup) => {
        setViewProjectGroupModalState({
            isOpen: true,
            projectGroupMembership,
        });
    }, []);
    const closeViewProjectGroupModal = useCallback(
        () =>
            setViewProjectGroupModalState({
                isOpen: false,
                projectGroupMembership: null,
            }),
        [],
    );

    const openProjectSubmissionModal = useCallback(
        (projectGroupMembership?: ProjectGroupMembershipSansProjectGroup | null) =>
            setProjectSubmissionModalState({
                isOpen: true,
                projectGroupMembership: projectGroupMembership ?? null,
            }),
        [],
    );
    const closeProjectSubmissionModal = useCallback(
        () =>
            setProjectSubmissionModalState({
                isOpen: false,
                projectGroupMembership: null,
            }),
        [],
    );

    const handleCreateGroupFromSubmission = useCallback(() => {
        setProjectSubmissionModalState({
            isOpen: false,
            projectGroupMembership: null,
        });
        setProjectGroupSetupModalOpen(true);
    }, []);

    const handleViewGroupFromSubmission = useCallback(() => {
        setProjectSubmissionModalState(prevState => {
            if (prevState.projectGroupMembership) {
                setViewProjectGroupModalState({
                    isOpen: true,
                    projectGroupMembership: prevState.projectGroupMembership,
                });
            }

            return {
                isOpen: false,
                projectGroupMembership: prevState.projectGroupMembership,
            };
        });
    }, []);

    const modalHandlers = useMemo(
        () => ({
            openProjectGroupSetupModal,
            openProjectSubmissionModal,
            openViewProjectGroupModal,
        }),
        [openProjectGroupSetupModal, openProjectSubmissionModal, openViewProjectGroupModal],
    );

    return {
        closeProjectGroupSetupModal,
        closeProjectSubmissionModal,
        closeViewProjectGroupModal,
        handleCreateGroupFromSubmission,
        handleViewGroupFromSubmission,
        isProjectGroupSetupModalOpen,
        modalHandlers,
        projectSubmissionModalState,
        viewProjectGroupModalState,
    };
};

const ProjectContentBoxComponent: FC<ProjectContentBoxProps> = ({ project, fullWidth }) => {
    const imageSrc = useProjectImage();
    const pendingFocusedProjectRequirementIdentifier = useSelector(getPendingFocusedProjectRequirementIdentifier);
    const isPendingFocusedProject = pendingFocusedProjectRequirementIdentifier === project.requirementIdentifier;
    const forceExpanded = useForceExpanded(project.id);
    const {
        closeProjectGroupSetupModal,
        closeProjectSubmissionModal,
        closeViewProjectGroupModal,
        handleCreateGroupFromSubmission,
        handleViewGroupFromSubmission,
        isProjectGroupSetupModalOpen,
        modalHandlers,
        projectSubmissionModalState,
        viewProjectGroupModalState,
    } = useProjectModals();
    const { expandedContent, icon } = useProjectContentBoxExpandedContent({
        project,
        modalHandlers,
    });

    return (
        <>
            <ProjectContentBoxPresentation
                title={project.title}
                src={imageSrc}
                fullWidth={fullWidth}
                expandedContent={expandedContent}
                endIcon={icon}
                projectRequirementIdentifier={project.requirementIdentifier}
                initiallyExpanded={isPendingFocusedProject}
                itemId={project.id}
                forceExpanded={forceExpanded}
            />
            <ProjectGroupSetupModal
                closeModal={closeProjectGroupSetupModal}
                isOpen={isProjectGroupSetupModalOpen}
                project={project}
            />
            <ProjectSubmissionFormModal
                closeModal={closeProjectSubmissionModal}
                isOpen={projectSubmissionModalState.isOpen}
                project={project}
                projectGroupMembership={projectSubmissionModalState.projectGroupMembership}
                onCreateGroup={handleCreateGroupFromSubmission}
                onViewGroup={handleViewGroupFromSubmission}
            />
            <ViewProjectGroupModal
                closeModal={closeViewProjectGroupModal}
                isOpen={viewProjectGroupModalState.isOpen}
                project={project}
                projectGroupMembership={viewProjectGroupModalState.projectGroupMembership}
            />
        </>
    );
};

export const ProjectContentBox = memo(ProjectContentBoxComponent) as typeof ProjectContentBoxComponent;
